// src/components/CompanyAvatarUploader.jsx
import React, { useState, useRef } from "react";
import {
  Box, Button, CircularProgress, Typography, IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { doc, updateDoc, getDoc, getFirestore } from "firebase/firestore";

const CompanyAvatarUploader = ({
  userId,
  avatarUrl,
  onUpload,
  size = 80,
  compact = false,
  circleSx = {},
  deletable = true,           
  showDeleteOverlay = true,   
}) => {
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const label = "avatar";
  const capitalizedLabel = "Avatar";

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer.files?.[0]) await handleFileUpload(e.dataTransfer.files[0]);
  };

  const handleChange = async (e) => {
    if (e.target.files?.[0]) await handleFileUpload(e.target.files[0]);
  };

  const handleFileUpload = async (file) => {
    if (!file.type.match("image/(png|jpg|jpeg)")) {
      toast.error(`Please upload a PNG or JPG image for your ${label}.`); return;
    }
    if (file.size > 4.5 * 1024 * 1024) {
      toast.error("File size must be less than 4.5 MB."); return;
    }

    setLoading(true);
    try {
      const filename = `avatars/${userId}/${Date.now()}_${file.name}`;
      const response = await fetch(`/api/upload-avatar?filename=${encodeURIComponent(filename)}`, {
        method: "POST",
        body: file,
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API error:", errorText);
        throw new Error("Upload failed");
      }
      const blob = await response.json();
      const db = getFirestore();
      await updateDoc(doc(db, "users", userId), { avatar: blob.url, avatarPath: filename });
      onUpload?.(blob.url);
      toast.success(`${capitalizedLabel} successfully uploaded!`);
    } catch (error) {
      console.error(`Loading error ${label}:`, error);
      toast.error(`Failed to load ${label}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!avatarUrl) return;
    if (!window.confirm(`Are you sure you want to delete your ${label}?`)) return;

    setLoading(true);
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, "users", userId));
      const { avatarPath } = userDoc.data() || {};

      if (avatarPath) {
        const response = await fetch("/api/delete-avatar", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pathname: avatarPath }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Delete API error:", errorText);
          throw new Error("Delete failed");
        }

        await updateDoc(doc(db, "users", userId), { avatar: null, avatarPath: null });
        onUpload?.(null);
        toast.success(`${capitalizedLabel} successfully removed!`);
      }
    } catch (error) {
      console.error(`Error deleting ${label}:`, error);
      toast.error(`Failed to delete ${label}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center" }}>
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: dragActive ? "2px dashed #14F195" : "2px solid #ccc",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          cursor: "pointer",
         
          "&:hover .avatar-del": { opacity: 1 },
          ...circleSx,
        }}
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {loading ? (
          <CircularProgress size={Math.max(16, Math.round(size * 0.35))} />
        ) : avatarUrl ? (
          <img
            src={avatarUrl}
            alt={capitalizedLabel}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Typography
  variant="caption"
  sx={{
    textAlign: "center",
    px: 0.5,
    fontSize: size <= 56 ? 10 : 12,
    lineHeight: 1.1,
    opacity: 0.95,
    color: "#0f172a",        
    fontWeight: 800          
  }}
>
  Upload {capitalizedLabel}   {/* было: Upload {label} */}
</Typography>
        )}

        {/* Overlay "remove" - ​​on top of the circle*/}
        {avatarUrl && deletable && showDeleteOverlay && (
          <IconButton
            className="avatar-del"
            size="small"
            aria-label="Delete avatar"
            onClick={(e) => { e.stopPropagation(); handleDeleteAvatar(); }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 22,
              height: 22,
              opacity: 0.85,             
              backdropFilter: "blur(6px)",
              bgcolor: "rgba(15,23,42,.55)",
              border: "1px solid rgba(156, 153, 153, 0.18)",
              color: "#fff",
              p: "2px",
              "&:hover": { bgcolor: "rgba(239,68,68,.9)" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpg,image/jpeg"
          id={`avatar-upload-${userId}`}
          style={{ display: "none" }}
          onChange={handleChange}
        />
      </Box>

      {/* Bottom buttons - only if NOT compact*/}
      {!compact && (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            component="label"
            htmlFor={`avatar-upload-${userId}`}
            sx={{ fontSize: "0.75rem", py: 0.5 }}
          >
            Upload
          </Button>
          {avatarUrl && deletable && (
            <IconButton size="small" onClick={handleDeleteAvatar} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompanyAvatarUploader;

